import { useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, DialogTitle, Divider, Grid, Typography } from "@mui/material";
import FormField from "../../../../components/form-controls/form-field";
import theme from "../../../../app/app-theme";
import FormCloseButton from "../../../../components/form-controls/form-close-button";
import { FormTextField } from "../../../../components/form-input-field-wrappers/form-controller-inputs/form-text-field";
import { MealItem, MealItemRequest } from "../../../../service/model/nutrition.model";
import { FormSelectField } from "../../../../components/form-input-field-wrappers/form-controller-inputs/form-select-field";
import useUnits from "../../../../hooks/use-units";

interface MealItemFormProps {
    readonly mealItem?: MealItem
    readonly editable: boolean
    readonly onClose: () => void
    readonly onSubmit: (data: MealItemRequest) => void
}

const style = {
    // position: 'absolute' as 'absolute',
    // top: '50%',
    // left: '50%',
    // transform: 'translate(-50%, -50%)',
    // width: 400,
    // bgcolor: 'background.paper',
    // border: '2px solid #000',
    // boxShadow: 24,
    p: 2,
};

const MealItemForm: React.FC<MealItemFormProps> = ({ mealItem, editable, onClose, onSubmit }) => {
    const [units] = useUnits();

    const schema = yup.object().shape({
        name: yup.string()
            .required(),
        // category: yup.string(),
        defaultQuantity: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("Quantity is required")
            .min(0),
        defaultUnitId: yup.number()
            .transform((value) => Number.isNaN(value) ? null : value)
            .required("Unit is required"),
        calories: yup.number().nullable()
            .transform((value) => Number.isNaN(value) ? null : value),
        protein: yup.number().nullable()
            .transform((value) => Number.isNaN(value) ? null : value),
        carbohydrates: yup.number().nullable()
            .transform((value) => Number.isNaN(value) ? null : value),
        fat: yup.number().nullable()
            .transform((value) => Number.isNaN(value) ? null : value),
        fiber: yup.number().nullable()
            .transform((value) => Number.isNaN(value) ? null : value),
        sugar: yup.number().nullable()
            .transform((value) => Number.isNaN(value) ? null : value),
    });

    const { register, handleSubmit, watch, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const onSubmitHandler = (data: any) => {
        const request: MealItemRequest = {
            id: mealItem?.id,
            name: data.name,
            // category: data.category,
            defaultQuantity: data.defaultQuantity,
            defaultUnitId: data.defaultUnitId,
            nutrition: {
                calories: data.calories,
                protein: data.protein,
                carbohydrates: data.carbohydrates,
                fat: data.fat,
                fiber: data.fiber,
                sugar: data.sugar,
            },
        };
        onSubmit(request)
    };

    const selectedUnitId = watch("defaultUnitId");
    const selectedUnit = units.find(u => u.id === Number(selectedUnitId));
    const selectedQuantity = watch("defaultQuantity");

    return (
        <Dialog open={true} onClose={onClose} sx={style} maxWidth="sm">
            <DialogTitle>Meal Item Details</DialogTitle>
            <Box sx={style}>
                <>
                    <FormCloseButton onClose={onClose} />
                    <form onSubmit={handleSubmit(onSubmitHandler)}>
                        <FormField>
                            <FormTextField
                                id="meal-item-name"
                                label="Meal Item Name"
                                name="name"
                                variant="outlined"
                                defaultValue={mealItem?.name}
                                errorMessage={errors.name?.message}
                                register={register}
                            />
                        </FormField>
                        {/* <FormField>
                            <FormTextField
                                id="meal-item-category"
                                label="Category"
                                name="category"
                                variant="outlined"
                                defaultValue={mealItem?.category}
                                errorMessage={errors.category?.message}
                                register={register}
                            />
                        </FormField> */}
                        <Grid container direction="row" justifyContent='space-between' sx={{ mt: 1 }}>
                            <FormField>
                                <FormSelectField
                                    id="default-unit"
                                    label="Unit"
                                    name="defaultUnitId"
                                    variant="outlined"
                                    defaultValue={mealItem?.defaultUnit?.id}
                                    errorMessage={errors.defaultUnitId?.message}
                                    register={register}
                                    options={units.map(unit => ({ value: unit.id, label: unit.name }))}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="default-quantity"
                                    type="number"
                                    label="Quantity"
                                    name="defaultQuantity"
                                    variant="outlined"
                                    disabled={selectedUnitId === undefined}
                                    defaultValue={mealItem?.defaultQuantity}
                                    errorMessage={errors.defaultQuantity?.message}
                                    register={register}
                                />
                            </FormField>
                        </Grid>
                        <Divider/>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1, pl: '16px' }}>
                            Nutrition information for selected quantity and unit {selectedQuantity && selectedUnit ? `(${selectedQuantity} ${selectedUnit.symbol})` : ''}:
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, pl: '16px' }}>
                            Note: The nutrition information for this meal item can be updated later.
                        </Typography>
                        <Grid container direction="row" justifyContent='space-between' sx={{ mt: 1 }}>
                            <FormField>
                                <FormTextField
                                    id="calories"
                                    type="number"
                                    label="Calories (cal)"
                                    name="calories"
                                    variant="outlined"
                                    defaultValue={mealItem?.nutrition?.calories}
                                    errorMessage={errors.calories?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="protein"
                                    type="number"
                                    label="Protein (g)"
                                    name="protein"
                                    variant="outlined"
                                    defaultValue={mealItem?.nutrition?.protein}
                                    errorMessage={errors.protein?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="carbohydrates"
                                    type="number"
                                    label="Carbohydrates (g)"
                                    name="carbohydrates"
                                    variant="outlined"
                                    defaultValue={mealItem?.nutrition?.carbohydrates}
                                    errorMessage={errors.carbohydrates?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="fat"
                                    type="number"
                                    label="Fat (g)"
                                    name="fat"
                                    variant="outlined"
                                    defaultValue={mealItem?.nutrition?.fat}
                                    errorMessage={errors.fat?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="fiber"
                                    type="number"
                                    label="Fiber (g)"
                                    name="fiber"
                                    variant="outlined"
                                    defaultValue={mealItem?.nutrition?.fiber}
                                    errorMessage={errors.fiber?.message}
                                    register={register}
                                />
                            </FormField>
                            <FormField>
                                <FormTextField
                                    id="sugar"
                                    type="number"
                                    label="Sugar (g)"
                                    name="sugar"
                                    variant="outlined"
                                    defaultValue={mealItem?.nutrition?.sugar}
                                    errorMessage={errors.sugar?.message}
                                    register={register}
                                />
                            </FormField>
                        </Grid>
                        <Grid container direction="row" justifyContent='end' sx={{ mt: 1 }}>
                            <Button type="button" variant="contained" sx={{ mr: 1, bgcolor: `${theme.palette.secondary.main}` }} onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained" >Submit</Button>
                        </Grid>
                    </form>
                </>
            </Box>
        </Dialog>
    );
}

export default MealItemForm;